/**
 * Which model in a repo a request is for.
 *
 * Extracted from `main.ts` so the **share-card generator uses the same rule as
 * the page** (2026-08-10). A card that resolved `?part=` differently from the
 * app would draw a different model than the link it is previewing — the exact
 * class of drift the shared map renderer exists to prevent, one layer up.
 *
 * The two formats disagree about what a "part" is, which is the whole reason
 * this is more than a lookup. A safetensors component is a *directory* (`vae/`)
 * and a GGUF variant is a *file path*, so the safetensors matching cannot find
 * a GGUF key and appending a slash to one produces a string matching nothing.
 * A deep link to `?part=BF16/model-BF16-00001-of-00003.gguf` round-tripped
 * through the URL perfectly and then landed on the picker — the failure that
 * looks most like "the feature works" while losing exactly the state a shared
 * link exists to carry.
 *
 * Pure: a resolved repo's file list in, a component out, or a typed refusal
 * naming the choices. No I/O.
 */

import {
  type GgufVariant,
  autoGgufVariant,
  findGgufVariants,
  variantAsComponent,
} from "../gguf/variants.js";
import {
  ComponentChoiceRequired,
  UnsupportedFormat,
} from "../safetensors/inventory.js";
import {
  type RepoComponent,
  autoComponent,
  describeUnreadableRepo,
  findComponents,
} from "./components.js";
import type { ResolvedRepo } from "./hub.js";

export function isGgufRepo(repo: ResolvedRepo): boolean {
  return (
    findComponents(repo.files).length === 0 && findGgufVariants(repo.files).length > 0
  );
}

export function chooseComponent(
  repo: ResolvedRepo,
  requestedPart?: string,
): RepoComponent {
  if (!requestedPart) return soleOrThrow(repo);

  /*
   * A GGUF variant key is a file path, not a directory prefix — so the
   * safetensors matching below cannot find it, and appending a slash to it
   * produces a string that matches nothing at all. A deep link to
   * `?part=BF16/model-BF16-00001-of-00003.gguf` threw `ComponentChoiceRequired`
   * before this branch existed: the link round-tripped through the URL
   * perfectly and then landed on the picker, which is the failure that looks
   * most like "the feature works" while losing exactly the state a shared link
   * exists to carry.
   */
  if (isGgufRepo(repo)) {
    const variants = findGgufVariants(repo.files);
    const match = variants.find(
      (v) => v.key === requestedPart || v.label === requestedPart,
    );
    if (match) return variantAsComponent(match);

    /*
     * **A label is not stable across uploads, and shared links carry labels.**
     *
     * `labelFor` strips the prefix shared by *every* candidate filename, so the
     * common stem — and therefore every label — depends on what else is in the
     * repo. When `unsloth/Qwen3.8-27B-GGUF` gained `MTP/mtp-Qwen3.8-27B-Q4_0`
     * the stem shortened and `UD-IQ1_S` became `Qwen3.8-27B-UD-IQ1_S`, so a
     * link shared the day before matched neither a key nor a label and landed
     * on the picker — the failure that looks most like the feature working,
     * since the page loads and simply asks a question it already had the answer
     * to.
     *
     * Resolved by stem rather than by changing what `?part=` carries. Switching
     * the URL to the key would fix links made *after* the change and abandon
     * every link already shared, which is the population that matters.
     *
     * **Unique match or nothing.** A suffix can be ambiguous — a repo holding
     * both `Q4_K_M` and `UD-Q4_K_M` has two labels ending in the same string —
     * and quietly opening one of two candidates is exactly the silent
     * wrong-subject selection this module refuses everywhere else. Two matches
     * fall through to the picker, where the visitor answers.
     */
    const suffix = variants.filter((v) => {
      const label = v.label;
      if (!label.endsWith(requestedPart)) return false;
      const before = label[label.length - requestedPart.length - 1];
      /*
       * `-`, `.` and `/` separate *names*; `_` separates *within* one. Allowing
       * `_` would let `K_M` resolve `Q4_K_M`, which is a fragment matching a
       * name — the same over-reach `AUXILIARY_ANYWHERE` was narrowed to avoid.
       * A stem is always stripped at a name boundary, so this loses nothing.
       */
      return before === "-" || before === "." || before === "/";
    });
    if (suffix.length === 1) return variantAsComponent(suffix[0] as GgufVariant);
    throw new ComponentChoiceRequired(
      repo.repoId,
      variants.map(variantAsComponent),
      "quantizations",
    );
  }

  const wanted = `${requestedPart.replace(/\/$/, "")}/`;
  const match = findComponents(repo.files).find(
    (c) => c.prefix === wanted || c.label === requestedPart,
  );
  if (match) return match;
  throw new ComponentChoiceRequired(repo.repoId, findComponents(repo.files));
}

/** The single-component path, which is `readInventory`'s own rule. */
function soleOrThrow(repo: ResolvedRepo): RepoComponent {
  const components = findComponents(repo.files);

  /*
   * GGUF, before the "no safetensors" refusal — which used to fire here and
   * meant a GGUF repo could not load at all, however complete the reader was.
   * The whole format was unreachable from the UI because the component was
   * resolved one function earlier than the discovery that knows about it.
   */
  if (components.length === 0) {
    const variants = findGgufVariants(repo.files);
    if (variants.length > 0) {
      const auto = autoGgufVariant(variants);
      if (!auto) {
        throw new ComponentChoiceRequired(
          repo.repoId,
          variants.map(variantAsComponent),
          "quantizations",
        );
      }
      return variantAsComponent(auto);
    }
    /*
     * Named from the files rather than guessed. This used to assert "PyTorch
     * .bin repos aren't supported yet" whatever was actually in the repo, and
     * told a CoreML conversion it was PyTorch — see `describeUnreadableRepo`.
     */
    throw new UnsupportedFormat(
      `${repo.repoId} has no safetensors or GGUF files at revision ${repo.sha.slice(0, 7)}. ${describeUnreadableRepo(repo.files)}, which this tool can't read — it works on safetensors and GGUF.`,
    );
  }

  const auto = autoComponent(components);
  if (!auto) throw new ComponentChoiceRequired(repo.repoId, components);
  return auto;
}
